import { ANNOUNCE_FPT_VOICES, announceRuleOf, announceSpeech, speechFor, type AnnounceKind, type AnnounceListener, type AnnounceVoice } from '@mt/shared';
import { useSyncExternalStore } from 'react';
import { api } from '@/lib/api';

/**
 * Âm báo "Minh Tiến" + đọc to việc giao khi ERP đang mở (DEC-671).
 *  - Âm báo: 3 nốt chuông tự tổng hợp bằng Web Audio (không cần file âm thanh, chạy cả khi mất mạng).
 *  - Đọc to: giọng tiếng Việt có sẵn trong máy (speechSynthesis), không tốn tiền khóa giọng FPT, không gửi dữ liệu ra ngoài.
 *  - Cài đặt theo từng máy (localStorage): điện thoại tài xế bật đọc to, máy tính văn phòng có thể chỉ để chuông.
 *  - Trình duyệt chỉ cho phát tiếng sau lần chạm đầu tiên vào trang → installAnnounceUnlock mở khóa ở lần chạm đầu.
 *  - Nhiều tab cùng mở: mỗi thông báo chỉ một tab phát (khóa theo id trong localStorage, tab đang xem được ưu tiên).
 */
export interface AnnounceSettings {
  chime: boolean;
  speak: boolean;
  volume: number; // 0.2 … 1
  rate: number; // tốc độ đọc 0.7 … 1.3
  kinds: Record<AnnounceKind, boolean>;
  /** Giữ màn hình sáng trong khung giờ của nhóm (để nghe đọc). */
  wake: boolean;
  /** Tài xế: cắm sạc thì giữ sáng suốt (Android tự biết đang sạc; iPhone không biết → bật tay khi cắm sạc trên xe). */
  wakeCharging: boolean;
  /** Giọng đọc: giọng của máy, hoặc giọng FPT qua máy chủ. Chọn giọng máy mà máy không có giọng Việt → tự dùng FPT Kim Ngân. */
  voice: AnnounceVoice;
}

export const ANNOUNCE_DEFAULTS: AnnounceSettings = {
  chime: true,
  speak: true,
  volume: 1,
  rate: 1,
  kinds: { assign: true, done: true, progress: true, remind: true },
  wake: true,
  wakeCharging: true,
  voice: 'device',
};

const KEY = 'mt.announce.v1';
const SEEN_KEY = 'mt.announce.seen';
const listeners = new Set<() => void>();
let cache: AnnounceSettings | null = null;

const clamp = (v: unknown, lo: number, hi: number, d: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);

export function readAnnounceSettings(): AnnounceSettings {
  if (cache) return cache;
  let saved: Partial<AnnounceSettings> | null = null;
  try {
    saved = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<AnnounceSettings> | null;
  } catch {
    saved = null;
  }
  const d = ANNOUNCE_DEFAULTS;
  cache = {
    chime: typeof saved?.chime === 'boolean' ? saved.chime : d.chime,
    speak: typeof saved?.speak === 'boolean' ? saved.speak : d.speak,
    volume: clamp(saved?.volume, 0.2, 1, d.volume),
    rate: clamp(saved?.rate, 0.7, 1.3, d.rate),
    wake: typeof saved?.wake === 'boolean' ? saved.wake : d.wake,
    wakeCharging: typeof saved?.wakeCharging === 'boolean' ? saved.wakeCharging : d.wakeCharging,
    voice: saved?.voice === 'device' || (ANNOUNCE_FPT_VOICES as readonly string[]).includes(saved?.voice as string) ? (saved!.voice as AnnounceVoice) : d.voice,
    kinds: {
      assign: typeof saved?.kinds?.assign === 'boolean' ? saved.kinds.assign : d.kinds.assign,
      done: typeof saved?.kinds?.done === 'boolean' ? saved.kinds.done : d.kinds.done,
      progress: typeof saved?.kinds?.progress === 'boolean' ? saved.kinds.progress : d.kinds.progress,
      remind: typeof saved?.kinds?.remind === 'boolean' ? saved.kinds.remind : d.kinds.remind,
    },
  };
  return cache;
}

export function saveAnnounceSettings(change: Partial<Omit<AnnounceSettings, 'kinds'>> & { kinds?: Partial<AnnounceSettings['kinds']> }) {
  const cur = readAnnounceSettings();
  cache = { ...cur, ...change, kinds: { ...cur.kinds, ...change.kinds } };
  try {
    localStorage.setItem(KEY, JSON.stringify(cache));
  } catch {
    /* bị chặn bộ nhớ: vẫn đổi trong phiên này */
  }
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export const useAnnounceSettings = () => useSyncExternalStore(subscribe, readAnnounceSettings, readAnnounceSettings);

// ---------------------------------------------------------------- Âm thanh
type AudioCtor = typeof AudioContext;
let ctx: AudioContext | null = null;
function audio(): AudioContext | null {
  if (ctx) return ctx;
  const C = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext) as AudioCtor | undefined;
  if (!C) return null;
  try {
    ctx = new C();
  } catch {
    ctx = null;
  }
  return ctx;
}

/** Ba nốt "Minh – Tiến – ♪" (Sol – Đô – Mi, kiểu chuông cửa), dài ~0,9 s. Trả về thời gian (ms) tới khi hết tiếng. */
export function playChime(volume = 1): number {
  const ac = audio();
  if (!ac) return 0;
  if (ac.state === 'suspended') void ac.resume().catch(() => undefined);
  const notes: Array<[number, number, number]> = [
    // [tần số Hz, bắt đầu s, độ dài s]
    [783.99, 0, 0.32], // G5 "Minh"
    [1046.5, 0.18, 0.36], // C6 "Tiến"
    [1318.51, 0.38, 0.55], // E6 ngân
  ];
  const t0 = ac.currentTime + 0.02;
  const master = ac.createGain();
  master.gain.value = 0.35 * Math.min(1, Math.max(0.1, volume));
  master.connect(ac.destination);
  for (const [f, start, dur] of notes) {
    for (const [mult, amp] of [
      [1, 1],
      [2, 0.25],
    ] as const) {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = 'sine';
      o.frequency.value = f * mult;
      g.gain.setValueAtTime(0.0001, t0 + start);
      g.gain.exponentialRampToValueAtTime(amp, t0 + start + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
      o.connect(g).connect(master);
      o.start(t0 + start);
      o.stop(t0 + start + dur + 0.05);
    }
  }
  return 950;
}

// ---------------------------------------------------------------- Đọc to
const synth = (): SpeechSynthesis | null => (typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null);

/** Giọng tiếng Việt của máy (Android: Google tiếng Việt; iPhone: Linh; Windows: An / HoaiMy). null nếu máy chưa cài. */
export function vietnameseVoice(): SpeechSynthesisVoice | null {
  const s = synth();
  if (!s) return null;
  const vs = s.getVoices().filter((v) => /^vi([-_]|$)/i.test(v.lang));
  if (!vs.length) return null;
  return vs.find((v) => v.localService) ?? vs[0] ?? null;
}

/** Đợi danh sách giọng tải xong (Chrome tải chậm sau khi mở trang). */
export function whenVoicesReady(timeoutMs = 2000): Promise<SpeechSynthesisVoice | null> {
  const s = synth();
  if (!s) return Promise.resolve(null);
  if (s.getVoices().length) return Promise.resolve(vietnameseVoice());
  return new Promise((res) => {
    const done = () => {
      s.removeEventListener('voiceschanged', done);
      res(vietnameseVoice());
    };
    s.addEventListener('voiceschanged', done);
    setTimeout(done, timeoutMs);
  });
}

/**
 * ok: giọng Việt của máy; server: máy không có giọng Việt → đọc bằng giọng FPT trên máy chủ;
 * no-voice: không có giọng Việt và máy chủ chưa có khóa FPT → chỉ reo chuông.
 */
export function speakSupport(st: AnnounceSettings = readAnnounceSettings()): 'ok' | 'server' | 'no-voice' {
  if (st.voice === 'device' && vietnameseVoice()) return 'ok';
  return serverVoiceBroken ? 'no-voice' : 'server';
}

function speakDevice(text: string, st: AnnounceSettings, voice: SpeechSynthesisVoice): Promise<void> {
  const s = synth()!;
  return new Promise((res) => {
    const u = new SpeechSynthesisUtterance(text);
    u.voice = voice;
    u.lang = voice.lang;
    u.rate = st.rate;
    u.volume = st.volume;
    const end = () => res();
    u.onend = end;
    u.onerror = end;
    // Chrome Android đôi khi không gọi onend → tự nhả sau thời gian ước tính
    setTimeout(end, 1500 + text.length * 120);
    s.speak(u);
  });
}

// Giọng FPT trên máy chủ (DEC-671): máy tính Windows / Mac thường không có giọng tiếng Việt
let serverVoiceBroken = false; // máy chủ chưa có khóa FPT / hết ngân sách → thôi gọi trong phiên này
const serverAudio = new Map<string, AudioBuffer>(); // cùng giọng + câu (nhắc lại) không tải lại

async function speakServer(text: string, st: AnnounceSettings): Promise<void> {
  const ac = audio();
  if (!ac || serverVoiceBroken) return;
  const voice = st.voice === 'device' ? 'kim_ngan' : st.voice;
  const key = `${voice}|${text}`;
  let buf = serverAudio.get(key);
  if (!buf) {
    try {
      const blob = await api.postBlob('/notifications/speak', { text: text.slice(0, 400), voice });
      buf = await ac.decodeAudioData(await blob.arrayBuffer());
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 503 || status === 403) serverVoiceBroken = true;
      listeners.forEach((l) => l());
      return;
    }
    serverAudio.set(key, buf);
    if (serverAudio.size > 50) serverAudio.delete(serverAudio.keys().next().value as string);
  }
  if (ac.state === 'suspended') await ac.resume().catch(() => undefined);
  const src = ac.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = st.rate;
  const g = ac.createGain();
  g.gain.value = st.volume;
  src.connect(g).connect(ac.destination);
  await new Promise<void>((res) => {
    src.onended = () => res();
    setTimeout(res, (buf.duration / st.rate) * 1000 + 1500);
    src.start();
  });
}

function speak(text: string, st: AnnounceSettings): Promise<void> {
  const voice = st.voice === 'device' ? vietnameseVoice() : null;
  if (synth() && voice) return speakDevice(text, st, voice);
  return speakServer(text, st);
}

// ---------------------------------------------------------------- Hàng đợi phát
let chain: Promise<void> = Promise.resolve();
let pending = 0;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Phát chuông rồi đọc câu; nhiều tin tới cùng lúc thì phát lần lượt, quá 3 tin chờ thì bỏ bớt (chỉ chuông). */
export function announceText(text: string | null, st: AnnounceSettings = readAnnounceSettings()): Promise<void> {
  if (!st.chime && !(st.speak && text)) return Promise.resolve();
  pending++;
  const crowded = pending > 3;
  chain = chain.then(async () => {
    try {
      if (st.chime) await sleep(playChime(st.volume));
      if (st.speak && text && !crowded) await speak(text, st);
    } finally {
      pending--;
    }
  });
  return chain;
}

/**
 * Thông báo vừa tới (realtime) có cần phát không.
 *  - Việc Khẩn (bắt buộc, anh Tiến 02/11/2026 "Nhiệm vụ bắt buộc nữa"): luôn phát, không tắt được.
 *  - Còn lại: đúng loại trong danh sách, nhóm đang bật, không phải Bản tin.
 */
export function shouldAnnounce(n: { type: string; level: string }, st: AnnounceSettings = readAnnounceSettings()): boolean {
  if (isMandatory(n)) return true;
  if (n.level === 'info') return false;
  const r = announceRuleOf(n.type);
  return !!r && st.kinds[r.kind] && (st.chime || st.speak);
}

export const isMandatory = (n: { level: string }) => n.level === 'urgent';

/** Chỉ một tab phát mỗi thông báo: tab đang xem nhận ngay, tab ẩn đợi chút rồi mới giành. */
async function claim(id: number): Promise<boolean> {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') await sleep(600);
  try {
    const seen = Number(localStorage.getItem(SEEN_KEY) ?? 0);
    if (id <= seen) return false;
    localStorage.setItem(SEEN_KEY, String(id));
  } catch {
    /* không có bộ nhớ: cứ phát */
  }
  return true;
}

/** Việc bắt buộc: nhắc lại mỗi 3 phút tới khi mở xem, tối đa 5 lần. */
export const MANDATORY_REPEAT_MS = 3 * 60_000;
export const MANDATORY_REPEAT_MAX = 5;
const repeats = new Map<number, ReturnType<typeof setTimeout>>();

/** Dừng nhắc lại (đã mở xem, đã đọc hết, đăng xuất). Không truyền id = dừng tất cả. */
export function stopAnnounceRepeat(id?: number) {
  if (id === undefined) {
    repeats.forEach((t) => clearTimeout(t));
    repeats.clear();
    return;
  }
  clearTimeout(repeats.get(id));
  repeats.delete(id);
}

/**
 * Phát một thông báo vừa tới. isUnread: hỏi lại thông báo còn chưa đọc không (để nhắc lại việc bắt buộc).
 */
export async function announceNotification(n: { id: number; type: string; title: string; message?: string | null; level: string }, isUnread: () => boolean = () => true) {
  const st = readAnnounceSettings();
  if (!shouldAnnounce(n, st)) return;
  if (!(await claim(n.id))) return;
  const mandatory = isMandatory(n);
  // Bắt buộc: luôn reo + đọc (nếu máy có giọng Việt), bỏ qua công tắc đã tắt
  const eff = mandatory ? { ...st, chime: true, speak: true, volume: Math.max(st.volume, 0.8) } : st;
  const text = mandatorySpeech(n, mandatory);
  await announceText(text, eff);
  if (!mandatory) return;
  let left = MANDATORY_REPEAT_MAX;
  const again = () => {
    repeats.delete(n.id);
    if (!isUnread() || left-- <= 0) return;
    void announceText(text, eff);
    repeats.set(n.id, setTimeout(again, MANDATORY_REPEAT_MS));
  };
  stopAnnounceRepeat(n.id);
  repeats.set(n.id, setTimeout(again, MANDATORY_REPEAT_MS));
}

function mandatorySpeech(n: { type: string; title: string; message?: string | null }, mandatory: boolean): string {
  if (!mandatory) return announceSpeech(n, { listener }) ?? '';
  const lead = announceRuleOf(n.type)?.lead;
  return speechFor(lead ? `Việc khẩn. ${lead}` : 'Việc khẩn', n, { listener });
}

// Chủ máy (người đang đăng nhập): máy gọi "Anh / Chị <tên> ơi" (anh Tiến 02/11/2026)
let listener: AnnounceListener | null = null;
export const setAnnounceListener = (l: AnnounceListener | null) => {
  listener = l;
};
/** Câu nghe thử, gọi đúng tên chủ máy. */
export const sampleSpeech = () =>
  speechFor('Có lệnh lấy hàng', { title: 'Xe 61H00609 lấy hàng ở Hòa Phát', message: 'Giao về kho Tân Vạn, 3 tấn' }, { listener });

/**
 * Bấm thông báo đẩy (màn khóa) → ERP mở ra và đọc ngay tin đó (DEC-671). Người dùng đã chủ động bấm nên đọc mọi loại,
 * chỉ cần máy bật "Đọc to" (việc Khẩn thì luôn đọc). Trình duyệt có thể chặn tiếng ở lần mở đầu chưa chạm → chỉ hiện chữ.
 */
export function announceOnOpen(n: { type: string; title: string; message?: string | null; level: string }) {
  const st = readAnnounceSettings();
  const mandatory = isMandatory(n);
  if (!mandatory && !st.speak && !st.chime) return Promise.resolve();
  const text = mandatory ? mandatorySpeech(n, true) : (announceSpeech(n, { listener }) ?? speechFor('', n, { listener }));
  return announceText(text, mandatory ? { ...st, chime: true, speak: true } : st);
}

/** Mở khóa âm thanh ở lần chạm / bấm phím đầu tiên (trình duyệt chặn phát tiếng khi người dùng chưa tương tác). */
export function installAnnounceUnlock() {
  if (typeof window === 'undefined') return () => undefined;
  const unlock = () => {
    const ac = audio();
    if (ac?.state === 'suspended') void ac.resume().catch(() => undefined);
    const s = synth();
    if (s) {
      // iPhone: lần đọc đầu phải nằm trong thao tác chạm → đọc một câu rỗng, tắt tiếng
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      s.speak(u);
      s.getVoices();
    }
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
  return () => {
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };
}
